/**
 * Toast placement, shared between the app-wide Toaster (ui/sonner.tsx) and the
 * per-user preference under Settings → Account.
 *
 * The Toaster is mounted above the router and outside any page context, so the
 * preference reaches it the same way the document language does: written onto
 * <html data-toast-position> by ToastPositionSync and observed here.
 */
import { useEffect, useState } from 'react';

export type ToastPosition = 'bottom-right' | 'top-right';

export const DEFAULT_TOAST_POSITION: ToastPosition = 'bottom-right';
export const TOAST_POSITION_ATTRIBUTE = 'data-toast-position';

/** Phones keep toasts at the top so they never cover the sticky bottom bars
 *  on public pages. 600px is sonner's own mobile breakpoint. */
export const TOAST_PHONE_QUERY = '(max-width: 600px)';

export function isToastPosition(value: unknown): value is ToastPosition {
  return value === 'bottom-right' || value === 'top-right';
}

export function readDocumentToastPosition(): ToastPosition {
  if (typeof document === 'undefined') return DEFAULT_TOAST_POSITION;
  const value = document.documentElement.getAttribute(TOAST_POSITION_ATTRIBUTE);
  return isToastPosition(value) ? value : DEFAULT_TOAST_POSITION;
}

export function useDocumentToastPosition(): ToastPosition {
  const [position, setPosition] = useState<ToastPosition>(readDocumentToastPosition);
  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
    const el = document.documentElement;
    const sync = () => setPosition(readDocumentToastPosition());
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: [TOAST_POSITION_ATTRIBUTE] });
    return () => observer.disconnect();
  }, []);
  return position;
}

/**
 * What else lives in the bottom-right corner. The Lumen bubble docks there
 * (higher on Messages), its open panel fills the corner, and the app-update
 * notice runs along the bottom. Each of them registers itself while mounted;
 * the Toaster reads the result off <html> and starts its stack above the
 * highest occupant, or leaves the right edge entirely while a panel is open.
 */
export const TOAST_CLEARANCE_ATTRIBUTE = 'data-toast-clearance-bottom';
export const TOAST_AVOID_RIGHT_ATTRIBUTE = 'data-toast-avoid-right';

export interface ToastClearance {
  /** Pixels between the viewport bottom and the toast stack. */
  bottom: number;
  /** The right edge is taken (an open panel): use the bottom centre instead. */
  avoidRight: boolean;
}

/** Clears the docked Lumen bubble (24px up, 56px tall) with a 16px gap. */
export const DEFAULT_TOAST_CLEARANCE: ToastClearance = { bottom: 96, avoidRight: false };

export interface ToastClearanceContribution {
  bottom?: number;
  avoidRight?: boolean;
}

const contributions = new Map<string, ToastClearanceContribution>();

function writeClearance(): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  let bottom = 0;
  let avoidRight = false;
  contributions.forEach((c) => {
    if (c.bottom && c.bottom > bottom) bottom = c.bottom;
    if (c.avoidRight) avoidRight = true;
  });
  if (bottom > 0) el.setAttribute(TOAST_CLEARANCE_ATTRIBUTE, String(Math.round(bottom)));
  else el.removeAttribute(TOAST_CLEARANCE_ATTRIBUTE);
  if (avoidRight) el.setAttribute(TOAST_AVOID_RIGHT_ATTRIBUTE, '1');
  else el.removeAttribute(TOAST_AVOID_RIGHT_ATTRIBUTE);
}

/** Register (or, with null, withdraw) one occupant of the corner. */
export function setToastClearance(id: string, contribution: ToastClearanceContribution | null): void {
  if (contribution) contributions.set(id, contribution);
  else contributions.delete(id);
  writeClearance();
}

/** Declarative form of setToastClearance for components: registered while mounted, withdrawn on unmount. */
export function useToastClearanceContributor(id: string, contribution: ToastClearanceContribution | null): void {
  const active = contribution !== null;
  const bottom = contribution?.bottom;
  const avoidRight = contribution?.avoidRight;
  useEffect(() => {
    setToastClearance(id, active ? { bottom, avoidRight } : null);
    return () => setToastClearance(id, null);
  }, [id, active, bottom, avoidRight]);
}

export function readDocumentToastClearance(): ToastClearance {
  if (typeof document === 'undefined') return DEFAULT_TOAST_CLEARANCE;
  const el = document.documentElement;
  const raw = Number(el.getAttribute(TOAST_CLEARANCE_ATTRIBUTE));
  const bottom = Number.isFinite(raw) && raw > DEFAULT_TOAST_CLEARANCE.bottom ? raw : DEFAULT_TOAST_CLEARANCE.bottom;
  return { bottom, avoidRight: el.getAttribute(TOAST_AVOID_RIGHT_ATTRIBUTE) === '1' };
}

export function useDocumentToastClearance(): ToastClearance {
  const [clearance, setClearance] = useState<ToastClearance>(readDocumentToastClearance);
  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
    const el = document.documentElement;
    const sync = () => {
      const next = readDocumentToastClearance();
      setClearance((prev) => (prev.bottom === next.bottom && prev.avoidRight === next.avoidRight ? prev : next));
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: [TOAST_CLEARANCE_ATTRIBUTE, TOAST_AVOID_RIGHT_ATTRIBUTE] });
    return () => observer.disconnect();
  }, []);
  return clearance;
}

function phoneMatches(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(TOAST_PHONE_QUERY).matches;
}

export function useIsPhoneForToasts(): boolean {
  const [isPhone, setIsPhone] = useState<boolean>(phoneMatches);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(TOAST_PHONE_QUERY);
    const sync = () => setIsPhone(mql.matches);
    sync();
    if ('addEventListener' in mql) {
      mql.addEventListener('change', sync);
      return () => mql.removeEventListener('change', sync);
    }
    // @ts-expect-error legacy API in some environments
    mql.addListener(sync);
    // @ts-expect-error legacy API in some environments
    return () => mql.removeListener(sync);
  }, []);
  return isPhone;
}
